import {
  ChangeDetectionStrategy, Component, computed, ElementRef, inject,
  signal, ViewChild,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ReactiveFormsModule, UntypedFormControl} from '@angular/forms';
import {Router} from '@angular/router';
import {Observable} from 'rxjs';
import {map, shareReplay} from 'rxjs/operators';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {OverlayModule, ScrollStrategy, ScrollStrategyOptions} from '@angular/cdk/overlay';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {DatePipe} from '@angular/common';
import {XmedicalUiModule} from '@xmedical/ui';
import {OrganisationRepository} from '../../../repositories/organisation.repository';
import {OrganisationStore} from '../../../stores/organisation.store';
import {OrgPatientSearchResult} from '../../../models/org-patient-search-result';
import {CohortGroup, groupSearchResultsByCohort} from '../../../utils/cohort-grouping';

/**
 * Toolbar-mounted org-wide patient search. Mirrors xmedical-app's
 * top-nav search look-and-feel: `xuiSearchBox` directive, fill
 * appearance, dimmed-until-focused, MDC overrides for vertical
 * centering of the input. Width is pinned to 60vw on desktop via CSS.
 *
 * On handset / tablet, the form collapses to a width-0 sliver and is
 * opened by a search icon button in the toolbar (`openSearch()`).
 *
 * Org-wide because the location-portal user is by definition
 * cross-practice. Results disclose the practice on each row and group
 * cohort siblings (same `linkGroupId`) under a single card. Selecting
 * a row navigates to that practice's patient view.
 */
@Component({
  selector: 'app-patient-omnisearch',
  imports: [
    ReactiveFormsModule,
    OverlayModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    DatePipe,
    XmedicalUiModule,
  ],
  templateUrl: './patient-omnisearch.component.html',
  styleUrl: './patient-omnisearch.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientOmnisearchComponent {
  private orgRepo = inject(OrganisationRepository);
  private orgStore = inject(OrganisationStore);
  private router = inject(Router);
  private scrollStrategyOptions = inject(ScrollStrategyOptions);
  private breakpointObserver = inject(BreakpointObserver);

  readonly formControl = new UntypedFormControl('');

  readonly results = signal<OrgPatientSearchResult[] | null>(null);
  readonly isSearching = signal(false);
  readonly isVisible = signal(false);

  private readonly isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(map(r => r.matches), shareReplay(1));
  private readonly isTablet$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Tablet)
    .pipe(map(r => r.matches), shareReplay(1));

  readonly isHandset = toSignal(this.isHandset$, {initialValue: false});
  readonly isTablet = toSignal(this.isTablet$, {initialValue: false});
  readonly isCompact = computed(() => this.isHandset() || this.isTablet());

  readonly groupedResults = computed<CohortGroup[]>(() =>
    groupSearchResultsByCohort(this.results() ?? [])
  );
  readonly hasResults = computed(() => (this.results()?.length ?? 0) > 0);
  // Forward typing is gated by `minCharacters` (2) inside `xuiSearchBox` —
  // no query fires until the user reaches the threshold. The reverse path
  // is deliberately *not* gated: results stay visible while the user
  // backspaces, all the way down to length 1, and only clear at length 0
  // (xuiSearchBox cancels there). Patient names in the result list are
  // full strings, so the brief mismatch between a 1-char input and the
  // last result set is unambiguous and matches common search UX (the
  // user can still pick a known patient mid-edit). Cleared on length 0.
  readonly resultsReady = computed(() =>
    !this.isSearching()
    && this.results() !== null
    && (this.formControl.value ?? '').length > 0
  );
  readonly showNoResults = computed(() =>
    this.resultsReady() && (this.results()?.length ?? 0) === 0
  );

  readonly overlayPanelClass = computed(() =>
    this.isCompact()
      ? ['omnisearch-overlay', 'omnisearch-overlay--compact']
      : ['omnisearch-overlay']
  );

  /** `xuiSearchBox` invokes this with the trimmed query. */
  readonly searchObservable = (query: string): Observable<OrgPatientSearchResult[]> => {
    const orgId = this.orgStore.selectedOrganisationId();
    if (!orgId) {
      return new Observable<OrgPatientSearchResult[]>(s => { s.next([]); s.complete(); });
    }
    return this.orgRepo.searchOrgPatients(orgId, query.toLowerCase());
  };

  readonly scrollStrategy: ScrollStrategy = this.scrollStrategyOptions.reposition();

  @ViewChild('host') hostElement!: ElementRef<HTMLElement>;

  /** Toolbar icon-button hook for compact viewports. */
  openSearch(): void {
    this.isVisible.set(true);
    queueMicrotask(() => {
      this.hostElement?.nativeElement?.querySelector<HTMLInputElement>('input')?.focus();
    });
  }

  closeSearch(): void {
    this.cancelSearch();
    this.isVisible.set(false);
  }

  onSearchResults(results: OrgPatientSearchResult[] | null): void {
    this.results.set(results ?? []);
  }

  onSearchStatusChange(searching: boolean): void {
    this.isSearching.set(searching);
  }

  onSearchError(_message: string): void {
    this.results.set([]);
  }

  selectResult(result: OrgPatientSearchResult): void {
    this.router.navigate(['/practices', result.practiceId, 'patients', result.profileId]);
    this.closeSearch();
  }

  cancelSearch(): void {
    this.formControl.setValue('');
    this.results.set(null);
    this.isSearching.set(false);
  }

  formatProfileId(result: OrgPatientSearchResult): string {
    return result.profileId?.toString().padStart(4, '0') ?? '';
  }

  cohortHeader(group: CohortGroup): string {
    const sample = group.members[0];
    return sample.displayName || sample.fullName || sample.name || '';
  }
}
